import { OfferProductSelectionView, OfferWizardShellView } from '@today/ui-domain'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { OFFERS_NAMESPACE } from '@/i18n'
import { useOfferProductSelection } from '@/modules/offer-wizard/useOfferProductSelection'
import { nextStep, OfferWizardStepId, stepIndexOf } from './offer-wizard-steps'

/**
 * `/offers/:offerId/products` — Offer Wizard Step 2, product selection (TO-123),
 * in the wizard shell: Next opens Step 3; no Back (the recipient is fixed once
 * the draft exists — TO-125 REQ-101).
 */
export function OfferProductSelectionPage() {
	const { offerId = '' } = useParams()
	const { t } = useTranslation(OFFERS_NAMESPACE)
	const navigate = useNavigate()
	const next = nextStep(OfferWizardStepId.PRODUCTS)
	return (
		<OfferWizardShellView
			t={t}
			activeStep={stepIndexOf(OfferWizardStepId.PRODUCTS)}
			error={null}
			onNext={next ? () => void navigate(next.path(offerId)) : undefined}
		>
			<ProductSelectionStep offerId={offerId} />
		</OfferWizardShellView>
	)
}

function ProductSelectionStep({ offerId }: { offerId: string }): ReactNode {
	const step = useOfferProductSelection(offerId)

	if (step.loadFailed) {
		return (
			<p role="alert" className="text-sm text-ds-danger">
				Could not load this offer.
			</p>
		)
	}
	if (step.loading) {
		return (
			<p className="text-sm text-ds-text-muted" aria-live="polite">
				Loading products…
			</p>
		)
	}
	return (
		<section className="flex flex-col gap-4">
			<h1 className="text-xl font-semibold">Step 2 — Products</h1>
			<OfferProductSelectionView
				catalogue={step.catalogue}
				selected={step.selected}
				readOnly={!step.editable}
				canSave={step.canSave}
				saving={step.saving}
				message={step.saveError ?? step.readOnlyMessage}
				onAdd={step.add}
				onMoveUp={step.moveUp}
				onMoveDown={step.moveDown}
				onRemove={step.remove}
				onSave={step.save}
			/>
		</section>
	)
}
